import { validateMonthlyPlan, type MonthlyPlan, type MonthlyIncome, type MonthlyEntry } from './monthly-plan';
type StoredPlan = Omit<MonthlyPlan,'entries'|'incomes'> & {entryIds:string[];incomeIds?:string[]};
function entryIds(data:StoredPlan):string[]{
  if(!Array.isArray(data.entryIds)||data.entryIds.length>30||new Set(data.entryIds).size!==data.entryIds.length||data.entryIds.some(id=>typeof id!=='string'||!/^[a-zA-Z0-9_-]{1,80}$/.test(id)))throw new Error('La planeación mensual no es válida.');
  return data.entryIds;
}
function incomeIds(data:StoredPlan):string[]{
  const ids=data.incomeIds??[];
  if(!Array.isArray(ids)||ids.length>20||new Set(ids).size!==ids.length||ids.some(id=>typeof id!=='string'||!/^[a-zA-Z0-9_-]{1,80}$/.test(id)))throw new Error('El desglose de ingresos no es válido.');
  return ids;
}
async function readStored(transaction:import('firebase/firestore').Transaction,ref:import('firebase/firestore').DocumentReference,stored:StoredPlan):Promise<MonthlyPlan>{
  const {doc}=await import('firebase/firestore');
  const [rows,incomes]=await Promise.all([
    Promise.all(entryIds(stored).map(id=>transaction.get(doc(ref,'entries',id)))),
    Promise.all(incomeIds(stored).map(id=>transaction.get(doc(ref,'incomes',id))))
  ]);
  if([...rows,...incomes].some(row=>!row.exists()))throw new Error('Falta un concepto de la planeación. Vuelve a abrirla.');
  const {entryIds:_,incomeIds:__,...metadata}=stored;
  return validateMonthlyPlan({...metadata,entries:rows.map(row=>row.data() as MonthlyEntry),...(stored.incomeIds!==undefined?{incomes:incomes.map(row=>row.data() as MonthlyIncome)}:{})});
}
export async function readMonthlyPlan(uid:string,month:string):Promise<MonthlyPlan|null>{
  const [{doc,runTransaction},{firestore}]=await Promise.all([import('firebase/firestore'),import('./firebase')]);
  const ref=doc(firestore,'users',uid,'monthlyPlans',month);
  return runTransaction(firestore,async transaction=>{
    const result=await transaction.get(ref);if(!result.exists())return null;
    const stored=result.data() as StoredPlan;
    return readStored(transaction,ref,stored);
  });
}
/** Planning and its operation record are atomic. No cash or historical period is written. */
export async function saveMonthlyPlan(uid:string,draft:MonthlyPlan,operationId:string):Promise<MonthlyPlan>{
  validateMonthlyPlan(draft);
  if(!/^monthly_[a-zA-Z0-9_-]{1,100}$/.test(operationId))throw new Error('La operación no es válida.');
  const request=JSON.stringify(draft);
  const [{doc,runTransaction},{firestore}]=await Promise.all([import('firebase/firestore'),import('./firebase')]);
  const ref=doc(firestore,'users',uid,'monthlyPlans',draft.month),operation=doc(firestore,'users',uid,'operations',operationId);
  return runTransaction(firestore,async transaction=>{
    const [previous,existing]=await Promise.all([transaction.get(ref),transaction.get(operation)]);
    const stored=previous.exists()?previous.data() as StoredPlan:null;
    if(existing.exists()){
      if(existing.data().request!==request||!stored)throw new Error('La operación ya se utilizó con otros datos.');
      return readStored(transaction,ref,stored);
    }
    if((stored?.revision??0)!==draft.revision)throw new Error('La planeación cambió en otro dispositivo. Cierra y vuelve a abrir para revisarla.');
    const saved=validateMonthlyPlan({...draft,revision:draft.revision+1,updatedAt:new Date().toISOString()});
    if(stored?.incomeIds!==undefined && draft.incomes===undefined)throw new Error('Actualiza Banculem para conservar el desglose de ingresos.');
    const {entries,incomes,...metadata}=saved;
    const ids=entries.map(entry=>entry.id);
    transaction.set(ref,{...metadata,entryIds:ids,...(incomes!==undefined?{incomeIds:incomes.map(row=>row.id)}:{})});
    for(const row of incomes??[])transaction.set(doc(ref,'incomes',row.id),row);
    for(const id of stored?incomeIds(stored):[])if(!incomes?.some(row=>row.id===id))transaction.delete(doc(ref,'incomes',id));
    for(const entry of entries)transaction.set(doc(ref,'entries',entry.id),entry);
    for(const id of stored?entryIds(stored):[])if(!ids.includes(id))transaction.delete(doc(ref,'entries',id));
    transaction.set(operation,{id:operationId,kind:'monthly_plan',createdAt:saved.updatedAt,month:draft.month,request,revision:saved.revision});
    return saved;
  });
}
